import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, FileWarning, CalendarClock, ShieldAlert, X, CheckCheck, FileSignature, ShoppingBag, HardHat, Mail, Clock } from "lucide-react";
import api, { formatCurrency, formatDate } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";

const SEV_COLORS = {
  alta: "#DC2626",
  media: "#D97706",
  baja: "#0EA5E9",
};

const ICONS = {
  aval_vencimiento: ShieldAlert,
  factura_vencida: FileWarning,
  licitacion_plazo: CalendarClock,
  garantia_estancada: AlertTriangle,
  firma_documento: FileSignature,
  microcompra_autorizacion: ShoppingBag,
  microcompra_limite_mes: ShoppingBag,
  microcompra_pico_semanal: ShoppingBag,
  produccion_supera_contrato: HardHat,
  produccion_partida_alerta_80: HardHat,
  produccion_sin_evidencias: HardHat,
  produccion_precert_excepciones: HardHat,
  email_nuevo: Mail,
  email_escalado_gerencia: ShieldAlert,
  plazo_legal_proximo: Clock,
  reunion_proxima: CalendarClock,
};

const READ_KEY = "legatum.notif.read";
const FILTER_KEY = "legatum.notif.filter";

function getRead() {
  try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) || "[]")); }
  catch { return new Set(); }
}
function setRead(set) {
  try { localStorage.setItem(READ_KEY, JSON.stringify([...set])); } catch {}
}

export default function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [read, setReadState] = useState(getRead());
  const [moduloFilter, setModuloFilter] = useState(() => {
    if (typeof window === "undefined") return "todos";
    return localStorage.getItem(FILTER_KEY) || "todos";
  });
  const nav = useNavigate();
  const ref = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(FILTER_KEY, moduloFilter); } catch { /* ignore */ }
  }, [moduloFilter]);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // refresh each minute
    return () => clearInterval(t);
  }, []);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const unread = items.filter((i) => !read.has(i.id));
  const totalUnread = unread.length;

  // Iter 127.c · módulos disponibles + filtrado por chips
  const modulosCount = items.reduce((acc, i) => {
    const m = i.modulo || "otros";
    acc[m] = (acc[m] || 0) + 1;
    return acc;
  }, {});
  const MOD_LABELS = {
    avales: "Avales", facturacion: "Facturas", microcompras: "Microcompras",
    cae: "CAE", pss: "PSS", licitaciones: "Licitaciones", obras: "Obras",
    documental: "Documental", garantias: "Garantías", produccion: "Producción",
    correo: "Correo",
  };
  const modulosVisibles = Object.keys(modulosCount).sort((a, b) => modulosCount[b] - modulosCount[a]);
  const itemsFiltrados = moduloFilter === "todos" ? items : items.filter((i) => i.modulo === moduloFilter);

  const markAll = () => {
    const next = new Set([...read, ...items.map((i) => i.id)]);
    setReadState(next);
    setRead(next);
  };

  const goTo = (item) => {
    const next = new Set([...read, item.id]);
    setReadState(next);
    setRead(next);
    setOpen(false);
    nav(item.ruta);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="notifications-bell"
        className="relative h-10 w-10 rounded-full flex items-center justify-center hover:bg-black/[0.05] dark:hover:bg-white/[0.06] transition-colors"
        aria-label="Notificaciones"
      >
        <Bell className="h-5 w-5 text-[var(--legatum-text-secondary)]" />
        {totalUnread > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[var(--legatum-bg)] tabular-nums"
          >
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.18 }}
            data-testid="notifications-panel"
            className="absolute right-0 mt-2 w-[360px] max-w-[92vw] rounded-xl border border-[var(--legatum-border)] bg-[var(--legatum-surface)] shadow-2xl z-50 overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--legatum-border)]">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold">Notificaciones</span>
                {totalUnread > 0 && (
                  <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-500/15 text-red-500">
                    {totalUnread} nuevas
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {totalUnread > 0 && (
                  <button
                    onClick={markAll}
                    data-testid="notifications-markall"
                    className="text-xs font-semibold px-2 py-1 rounded hover:bg-black/[0.05] dark:hover:bg-white/[0.06] text-[var(--legatum-text-secondary)] hover:text-[var(--legatum-text)] flex items-center gap-1"
                  >
                    <CheckCheck className="h-3.5 w-3.5" /> Marcar leídas
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="h-7 w-7 rounded hover:bg-black/[0.05] dark:hover:bg-white/[0.06] flex items-center justify-center"
                  aria-label="Cerrar"
                >
                  <X className="h-4 w-4 text-[var(--legatum-text-muted)]" />
                </button>
              </div>
            </div>

            {modulosVisibles.length > 1 && (
              <div className="flex items-center gap-1 px-3 py-2 border-b border-[var(--legatum-border)] overflow-x-auto" data-testid="notifications-filters">
                <button
                  onClick={() => setModuloFilter("todos")}
                  data-testid="notif-filter-todos"
                  className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full whitespace-nowrap transition-colors ${moduloFilter === "todos" ? "bg-[var(--legatum-accent)] text-white" : "bg-black/[0.05] dark:bg-white/[0.06] text-[var(--legatum-text-muted)] hover:text-[var(--legatum-text)]"}`}
                >
                  Todos · {items.length}
                </button>
                {modulosVisibles.map((m) => (
                  <button
                    key={m}
                    onClick={() => setModuloFilter(m)}
                    data-testid={`notif-filter-${m}`}
                    className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full whitespace-nowrap transition-colors ${moduloFilter === m ? "bg-[var(--legatum-accent)] text-white" : "bg-black/[0.05] dark:bg-white/[0.06] text-[var(--legatum-text-muted)] hover:text-[var(--legatum-text)]"}`}
                  >
                    {MOD_LABELS[m] || m} · {modulosCount[m]}
                  </button>
                ))}
              </div>
            )}

            <div className="max-h-[460px] overflow-y-auto" data-testid="notifications-list">
              {itemsFiltrados.length === 0 && (
                <div className="px-6 py-10 text-center">
                  <Bell className="h-8 w-8 mx-auto mb-3 text-[var(--legatum-text-muted)] opacity-50" />
                  <div className="text-sm font-semibold text-[var(--legatum-text)]">
                    {items.length === 0 ? "Sin alertas activas" : "Sin alertas para este módulo"}
                  </div>
                  <div className="mt-1 text-xs text-[var(--legatum-text-muted)]">
                    {items.length === 0 ? "Avisaremos cuando algo necesite tu atención." : "Cambia el filtro para ver otras alertas."}
                  </div>
                </div>
              )}
              {itemsFiltrados.map((n) => {
                const Icon = ICONS[n.tipo] || AlertTriangle;
                const sevColor = SEV_COLORS[n.severidad] || "#94A3B8";
                const isRead = read.has(n.id);
                return (
                  <button
                    key={n.id}
                    onClick={() => goTo(n)}
                    data-testid={`notification-${n.id}`}
                    style={{
                      background: isRead
                        ? undefined
                        : `linear-gradient(90deg, ${sevColor}1A 0%, ${sevColor}08 40%, transparent 100%)`,
                      borderLeft: isRead ? "4px solid transparent" : `4px solid ${sevColor}`,
                      boxShadow: !isRead && n.severidad === "alta" ? `inset 0 0 0 1px ${sevColor}33` : undefined,
                    }}
                    className={`w-full text-left px-4 py-3 border-b border-[var(--legatum-border)] last:border-b-0 hover:brightness-105 transition-colors flex gap-3 ${isRead ? "opacity-60" : ""}`}
                  >
                    <div
                      className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0 relative"
                      style={{ background: `${sevColor}33`, color: sevColor, border: `1px solid ${sevColor}66` }}
                    >
                      {!isRead && n.severidad === "alta" && (
                        <span className="absolute inset-0 rounded-lg animate-pulse opacity-40" style={{ background: `${sevColor}33` }} />
                      )}
                      <Icon className="h-4 w-4 relative" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm font-bold leading-tight">{n.titulo}</div>
                        {!isRead && <span className="mt-1 h-2 w-2 rounded-full shrink-0" style={{ background: sevColor }} />}
                      </div>
                      <div className="mt-1 text-xs text-[var(--legatum-text-secondary)] leading-snug">
                        {n.detalle}
                      </div>
                      <div className="mt-1.5 flex items-center justify-between text-[10px] text-[var(--legatum-text-muted)] uppercase tracking-widest font-bold">
                        <span>{n.modulo}</span>
                        {n.fecha && <span>{formatDate(n.fecha)}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
